import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, MagnifyingGlass, CaretUp, CaretDown } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BACKEND_URL } from '../config';

const MONTHS_MAP = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatMonth = (label) => { if (!label) return ''; const [y, m] = label.split('-'); return `${MONTHS_MAP[parseInt(m) - 1]} '${y.slice(2)}`; };

const PartyWiseSales = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total_amount');
  const [sortDir, setSortDir] = useState('desc');
  const [drilldownId, setDrilldownId] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${BACKEND_URL}/api/sales/party-wise`, { withCredentials: true });
      setData(res);
    } catch (err) { console.error(err.message);
      toast.error('Failed to load party-wise sales');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDrilldown = async (clientId) => {
    setDrilldownId(clientId);
    setDrilldownLoading(true);
    try {
      const { data: res } = await axios.get(`${BACKEND_URL}/api/sales/party-detail`, {
        params: { client_id: clientId },
        withCredentials: true,
      });
      setDrilldownData(res);
    } catch (err) { console.error(err.message);
      toast.error('Failed to load client details');
    } finally {
      setDrilldownLoading(false);
    }
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return null;
    return sortDir === 'desc' ? <CaretDown size={10} weight="bold" /> : <CaretUp size={10} weight="bold" />;
  };

  if (loading) return <div className="text-center py-6 text-muted-foreground text-sm">Loading party-wise report...</div>;
  if (!data || data.total_parties === 0) {
    return (
      <div className="bg-card border border-border rounded-sm p-5" data-testid="party-wise-sales-empty">
        <div className="flex items-center gap-2 mb-3">
          <Users size={18} weight="bold" className="text-primary" />
          <div className="text-sm font-semibold tracking-tight">Party-Wise Sales</div>
        </div>
        <div className="text-center py-6 text-muted-foreground text-sm">No sales data available</div>
      </div>
    );
  }

  const filtered = data.parties
    .filter(p => p.party.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortDir === 'desc' ? (bVal > aVal ? 1 : -1) : (aVal > bVal ? 1 : -1);
    });

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="party-wise-sales">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users size={18} weight="bold" className="text-primary" />
          <div className="text-sm font-semibold tracking-tight">Party-Wise Sales ({data.total_parties} clients)</div>
          <span className="text-xs text-muted-foreground ml-1">Total: ₹{data.total_amount.toLocaleString('en-IN')}</span>
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="h-8 pl-8 pr-3 text-sm border border-border rounded-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring w-48"
            data-testid="party-sales-search-input"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]" data-testid="party-wise-sales-table">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">#</th>
              <th className="text-left py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Client</th>
              <th
                className="text-right py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none"
                onClick={() => handleSort('total_amount')}
              >
                <span className="flex items-center justify-end gap-1">Total Sales <SortIcon col="total_amount" /></span>
              </th>
              <th
                className="text-center py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none"
                onClick={() => handleSort('count')}
              >
                <span className="flex items-center justify-center gap-1">Txns <SortIcon col="count" /></span>
              </th>
              <th className="text-left py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Industry</th>
              <th
                className="text-center py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none"
                onClick={() => handleSort('percentage')}
              >
                <span className="flex items-center justify-center gap-1">% Share <SortIcon col="percentage" /></span>
              </th>
              <th className="text-left py-2 px-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Last Sale</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, idx) => (
              <tr key={p.client_id} className="border-b border-border hover:bg-muted/30 transition-colors" data-testid={`party-sales-row-${idx}`}>
                <td className="py-2.5 px-3 text-xs text-muted-foreground">{idx + 1}</td>
                <td className="py-2.5 px-3">
                  <button
                    onClick={() => openDrilldown(p.client_id)}
                    className="text-sm font-medium hover:text-primary hover:underline transition-colors text-left"
                    data-testid={`party-sales-link-${idx}`}
                  >
                    {p.party}
                  </button>
                </td>
                <td className="py-2.5 px-3 text-sm text-right font-semibold">₹{p.total_amount.toLocaleString('en-IN')}</td>
                <td className="py-2.5 px-3 text-sm text-center">{p.count}</td>
                <td className="py-2.5 px-3">
                  <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground font-medium">{p.industry}</span>
                </td>
                <td className="py-2.5 px-3 text-sm text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${Math.min(p.percentage, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">{p.percentage}%</span>
                  </div>
                </td>
                <td className="py-2.5 px-3 text-xs text-muted-foreground">{p.last_sale || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-6 text-muted-foreground text-sm">No clients match your search</div>
      )}

      {/* Client Drill-Down Dialog */}
      <Dialog open={!!drilldownId} onOpenChange={(v) => { if (!v) { setDrilldownId(null); setDrilldownData(null); } }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="party-sales-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl tracking-tight flex items-center gap-2">
              <Users size={22} weight="bold" className="text-primary" />
              {drilldownData?.party || '...'}
            </DialogTitle>
          </DialogHeader>

          {drilldownLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading details...</div>
          ) : drilldownData ? (
            <div className="space-y-5">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total Sales', value: `₹${drilldownData.total_amount.toLocaleString('en-IN')}`, color: 'text-primary' },
                  { label: 'Transactions', value: drilldownData.transaction_count },
                  { label: 'Avg / Txn', value: `₹${drilldownData.avg_transaction.toLocaleString('en-IN')}` },
                  { label: 'Active Months', value: drilldownData.active_months },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-muted/50 rounded-sm p-3">
                    <div className="text-[9px] tracking-[0.15em] uppercase font-bold text-muted-foreground">{label}</div>
                    <div className={`text-lg font-bold mt-0.5 ${color || ''}`}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Range */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                <span>First: <strong className="text-foreground">{drilldownData.first_sale?.slice(0, 10) || '—'}</strong></span>
                <span>Last: <strong className="text-foreground">{drilldownData.last_sale?.slice(0, 10) || '—'}</strong></span>
                <span>Min: <strong className="text-foreground">₹{drilldownData.min_transaction.toLocaleString('en-IN')}</strong></span>
                <span>Max: <strong className="text-foreground">₹{drilldownData.max_transaction.toLocaleString('en-IN')}</strong></span>
                {drilldownData.industry !== '—' && <span>Industry: <strong className="text-foreground">{drilldownData.industry}</strong></span>}
                {drilldownData.region !== '—' && <span>Region: <strong className="text-foreground">{drilldownData.region}</strong></span>}
              </div>

              {/* Monthly Trend Chart */}
              {drilldownData.monthly_trend?.length > 0 && (
                <div>
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">Monthly Trend</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={drilldownData.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tickFormatter={formatMonth} stroke="hsl(var(--foreground))" style={{ fontSize: '10px' }} />
                      <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '10px' }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Sales']}
                        labelFormatter={formatMonth}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem', fontSize: '12px' }}
                      />
                      <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Top Items + MoM Growth side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Top Items */}
                {drilldownData.top_items?.length > 0 && (
                  <div>
                    <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">Top Items / Descriptions</div>
                    <div className="space-y-1.5">
                      {drilldownData.top_items.map(item => (
                        <div key={item.description} className="flex items-center justify-between">
                          <span className="text-xs truncate max-w-[140px]" title={item.description}>{item.description}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold">₹{item.amount.toLocaleString('en-IN')}</span>
                            <span className="text-[10px] text-muted-foreground">({item.percentage}%)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* MoM Growth */}
                {drilldownData.monthly_trend?.length > 1 && (
                  <div>
                    <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">Month-over-Month</div>
                    <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                      {drilldownData.monthly_trend.slice().reverse().map(m => (
                        <div key={m.month} className="flex items-center justify-between text-xs">
                          <span>{formatMonth(m.month)}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">₹{m.amount.toLocaleString('en-IN')}</span>
                            {m.growth !== null && (
                              <span className={`text-[10px] font-bold ${m.growth >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {m.growth >= 0 ? '+' : ''}{m.growth}%
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Transactions */}
              {drilldownData.recent_transactions?.length > 0 && (
                <div>
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">Recent Transactions</div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-1.5 px-2 text-[9px] tracking-[0.1em] uppercase font-bold text-muted-foreground">Date</th>
                          <th className="text-right py-1.5 px-2 text-[9px] tracking-[0.1em] uppercase font-bold text-muted-foreground">Amount</th>
                          <th className="text-left py-1.5 px-2 text-[9px] tracking-[0.1em] uppercase font-bold text-muted-foreground">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drilldownData.recent_transactions.map((t, i) => (
                          <tr key={i} className="border-b border-border">
                            <td className="py-1.5 px-2 text-xs">{t.date || '—'}</td>
                            <td className="py-1.5 px-2 text-xs text-right font-semibold">₹{t.amount.toLocaleString('en-IN')}</td>
                            <td className="py-1.5 px-2 text-xs text-muted-foreground truncate max-w-[200px]">{t.description || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <Button variant="outline" onClick={() => { setDrilldownId(null); setDrilldownData(null); }} className="w-full rounded-sm" data-testid="close-sales-drilldown-btn">
                Close
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartyWiseSales;
